import React, { useState, useEffect } from "react";
import { Section } from "../../../components/StructureElements/Section";
import { SectionHeader } from "../../my-profile/SectionHeader";
import { SectionHeaderTypography } from "../../../components/Typographies/SectionHeaderTypography";
import { SectionContent } from "../../my-profile/SectionContent";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { OrganizationLogoPicker } from "../../my-profile/subcomponents/organization-logo-picker";
import { ColorPickerEditorWrapper } from "../../my-profile/subcomponents/color-picker-editor.wrapper";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import { InputLabel } from "../../../components/Input/InputLabel";
import { CustomInput } from "../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../components/Input/CustomPhoneInput";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { CustomInputChangeEvent } from "../../../../utils/models/events/CustomInputChangeEvent";
import { Profile } from "../../../../core/models/entities/Profile";
import { useProfileRepository } from "../../../../data/repositories/ProfileRepository";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useApiClient } from "../../../../data/clients/ApiClient";

export interface WhiteLabelConfigurationsForm {
  colorHex: string;
  whiteLabelCopyrightName: string;
  whiteLabelContactEmail: string;
  whiteLabelContactPhone: string;
  whiteLabelVendorName: string;
  whiteLabelWebsiteLink: string;
}

export interface VendorWhiteLabelViewerProps {
  vendorId: number;
  disabled?: boolean;
}

export function VendorWhiteLabelViewer({
  vendorId,
  disabled = false,
}: VendorWhiteLabelViewerProps) {
  const { t } = useTranslation();
  const profileRepository = useProfileRepository();
  const {
    data: vendorProfile,
    isLoading,
    refetch,
  } = profileRepository.useProfile(vendorId);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<WhiteLabelConfigurationsForm>({
    colorHex: "",
    whiteLabelCopyrightName: "",
    whiteLabelContactEmail: "",
    whiteLabelContactPhone: "",
    whiteLabelVendorName: "",
    whiteLabelWebsiteLink: "",
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState(false);

  // Update form when vendor profile loads
  useEffect(() => {
    if (vendorProfile?.whiteLabelConfigurations) {
      setForm({
        whiteLabelVendorName:
          vendorProfile.whiteLabelConfigurations.whiteLabelVendorName || "",
        whiteLabelContactEmail:
          vendorProfile.whiteLabelConfigurations.whiteLabelContactEmail || "",
        whiteLabelContactPhone:
          vendorProfile.whiteLabelConfigurations.whiteLabelContactPhone || "",
        whiteLabelCopyrightName:
          vendorProfile.whiteLabelConfigurations.whiteLabelCopyrightName || "",
        whiteLabelWebsiteLink:
          vendorProfile.whiteLabelConfigurations.whiteLabelWebsiteLink || "",
        colorHex: vendorProfile.whiteLabelConfigurations.colorHex || "#7BBB58",
      });
    } else {
      // Set default values if whiteLabelConfigurations is null/undefined
      setForm({
        whiteLabelVendorName: "",
        whiteLabelContactEmail: "",
        whiteLabelContactPhone: "",
        whiteLabelCopyrightName: "",
        whiteLabelWebsiteLink: "",
        colorHex: "#7BBB58",
      });
    }
  }, [vendorProfile]);

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormColorHexChange = (
    updatedForm: WhiteLabelConfigurationsForm
  ) => {
    setForm(updatedForm);
  };

  const handleLogoChange = (file: File) => {
    setLogoFile(file);
    setPendingRemoval(false); // Cancel any pending removal when uploading
  };

  const handleLogoRemove = () => {
    setPendingRemoval(true);
    setLogoFile(null); // Clear any pending upload
  };

  const handleCancelRemove = () => {
    setPendingRemoval(false);
  };

  const apiClient = useApiClient();

  const handleSave = async () => {
    if (!vendorProfile) return;

    setIsSaving(true);
    try {
      // Handle logo changes first
      if (pendingRemoval) {
        await apiClient.deleteCompanyLogo(vendorId);
        setPendingRemoval(false);
      } else if (logoFile) {
        await apiClient.uploadCompanyLogo(vendorId, logoFile);
        setLogoFile(null);
      }

      const updatedProfile: Profile = {
        ...vendorProfile,
        whiteLabelConfigurations: {
          ...vendorProfile.whiteLabelConfigurations,
          colorHex: form.colorHex,
          whiteLabelCopyrightName: form.whiteLabelCopyrightName,
          whiteLabelContactEmail: form.whiteLabelContactEmail,
          whiteLabelContactPhone: form.whiteLabelContactPhone,
          whiteLabelVendorName: form.whiteLabelVendorName,
          whiteLabelWebsiteLink: form.whiteLabelWebsiteLink,
        },
      };

      await profileRepository.updateProfileById(vendorId, updatedProfile);
      await refetch();
      toast.success(t("orgScreens.whiteLabelUpdateSuccess"));
    } catch (error) {
      console.error("Error updating vendor white label settings:", error);
      toast.error(t("orgScreens.whiteLabelUpdateFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  if (isLoading) {
    return (
      <Section className={"!py-0"}>
        <SectionHeader isCloseable initialState={false} onToggle={() => {}}>
          <SectionHeaderTypography>
            {t("orgScreens.whiteLabelSettings")}
          </SectionHeaderTypography>
        </SectionHeader>
        <SectionContent close={true}>
          <div>{t("orgScreens.whiteLabelLoading")}</div>
        </SectionContent>
      </Section>
    );
  }

  return (
    <Section className={"!py-0"}>
      <SectionHeader
        isCloseable
        initialState={isExpanded}
        onToggle={handleToggle}
      >
        <SectionHeaderTypography>
          {t("orgScreens.whiteLabelSettings")}
        </SectionHeaderTypography>
      </SectionHeader>
      <SectionContent close={!isExpanded}>
        <StrucRow>
          <StrucCol size={3.5}>
            <InputWrapper>
              <InputLabel>{t("orgScreens.companyDisplayName")}</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (lf) => lf.whiteLabelVendorName
                )}
                value={form.whiteLabelVendorName}
                onChange={handleFormChange}
                disabled={disabled}
              />
            </InputWrapper>
            <ColorPickerEditorWrapper
              form={form}
              onFormChange={handleFormColorHexChange}
              disabled={disabled}
            />
            <InputWrapper>
              <InputLabel>{t("orgScreens.websiteLink")}</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (lf) => lf.whiteLabelWebsiteLink
                )}
                value={form.whiteLabelWebsiteLink}
                onChange={handleFormChange}
                disabled={disabled}
              />
            </InputWrapper>
          </StrucCol>
          <StrucCol size={3.5} offset={0.5}>
            <InputWrapper>
              <InputLabel>{t("email")}</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (lf) => lf.whiteLabelContactEmail
                )}
                value={form.whiteLabelContactEmail}
                onChange={handleFormChange}
                disabled={disabled}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t("orgScreens.phone")}</InputLabel>
              <CustomPhoneInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (lf) => lf.whiteLabelContactPhone
                )}
                value={form.whiteLabelContactPhone}
                onChange={handleFormChange}
                disabled={disabled}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{t("orgScreens.copyrightName")}</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (lf) => lf.whiteLabelCopyrightName
                )}
                value={form.whiteLabelCopyrightName}
                onChange={handleFormChange}
                disabled={disabled}
              />
            </InputWrapper>
          </StrucCol>
          <StrucCol size={3.5} offset={0.5}>
            <OrganizationLogoPicker
              value={vendorProfile?.whiteLabelConfigurations?.logoUrl || ""}
              onLogoChange={handleLogoChange}
              onLogoRemove={handleLogoRemove}
              onCancelRemove={handleCancelRemove}
              disabled={disabled}
              pendingRemoval={pendingRemoval}
            />
          </StrucCol>
        </StrucRow>
        {!disabled && (
          <StrucRow>
            <StrucCol size={12}>
              <div className="flex justify-end mt-4">
                <PrimaryButton
                  onClick={handleSave}
                  disabled={isSaving}
                  className="min-w-32"
                >
                  {isSaving ? t("orgScreens.saving") : t("save_changes")}
                </PrimaryButton>
              </div>
            </StrucCol>
          </StrucRow>
        )}
      </SectionContent>
    </Section>
  );
}
